// ===========================================
// Onboarding check-in API (enquiries Phase 6, plan §11a.7)
// ===========================================
// Diffed against `apps/backend/src/services/settlingIn/list.ts`, `tick.ts` and the item routes in
// `routes/settlingIn.routes.ts`. `firstClassDate` and `dueDate` are `@db.Date` values
// (`YYYY-MM-DDT00:00:00.000Z`): read them with `formatUTCDate`, never date-fns. The state chip and
// the due day are the SERVER's (the school's today), never computed here.

import { apiClient } from '../services/api';
import type { EnquiryStage } from './enquiry.api';

interface ApiResponse<T> {
  status: string;
  data: T;
}

export type SettlingInStatus = 'open' | 'due' | 'done' | 'all';
export type SettlingInState = 'NOT_SCHEDULED' | 'IN_PROGRESS' | 'DUE_TODAY' | 'OVERDUE' | 'DONE';

export interface SettlingInItem {
  id: string;
  label: string;
  sortOrder: number;
  done: boolean;
  completedAt: string | null;
  completedByName: string | null;
}

export interface SettlingInRow {
  studentId: string;
  studentName: string;
  family: { id: string; name: string } | null;
  classes: string[];
  origin: 'ENROLMENT' | 'TRIAL' | 'BACKFILL';
  /** `@db.Date`; null = no class scheduled yet. */
  firstClassDate: string | null;
  /** `@db.Date`; null while the first class is unknown. */
  dueDate: string | null;
  state: SettlingInState;
  completedAt: string | null;
  done: number;
  total: number;
  items: SettlingInItem[];
  /** Present only for a caller with `enquiries.read`. */
  enquiries?: Array<{ id: string; stage: EnquiryStage }>;
}

export interface SettlingInList {
  rows: SettlingInRow[];
  total: number;
  page: number;
  pageSize: number;
  /** `@db.Date`: the school's today. */
  schoolToday: string;
  windowDays: number;
}

export interface SettlingInTickResult {
  studentId: string;
  itemId: string;
  done: boolean;
  complete: boolean;
  /** Linked enquiries moved to Enrolled straight after this tick (the school's switch on). */
  enquiriesMoved: number;
}

/** A school's checklist item (an `EnquiryListOption` of kind ONBOARDING_ITEM). */
export interface ChecklistItemOption {
  id: string;
  schoolId: string;
  kind: 'ONBOARDING_ITEM';
  label: string;
  systemKey: string | null;
  isSystem: boolean;
  isSelectable: boolean;
  isActive: boolean;
  sortOrder: number;
  /** Owner-tier reads only. */
  createdById?: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
}

const base = '/admin/settling-in';

export const settlingInApi = {
  list: (params: { status?: SettlingInStatus; page?: number } = {}) =>
    apiClient.get<ApiResponse<SettlingInList>>(base, { params }).then((res) => res.data),

  tick: (studentId: string, itemId: string, done: boolean) =>
    apiClient
      .post<ApiResponse<SettlingInTickResult>>(
        `${base}/${encodeURIComponent(studentId)}/items/${encodeURIComponent(itemId)}/${done ? 'tick' : 'untick'}`,
      )
      .then((res) => res.data),

  /** The active items; with `includeHidden`, the hidden ones too (honoured for the owner tier only). */
  listItems: (opts: { includeHidden?: boolean } = {}) =>
    apiClient
      .get<ApiResponse<ChecklistItemOption[]>>(`${base}/items`, { params: opts.includeHidden ? { includeHidden: 'true' } : {} })
      .then((res) => res.data),

  /** The owner tier only. `existed` sits BESIDE `data`. */
  addItem: (label: string) =>
    apiClient
      .post<ApiResponse<ChecklistItemOption> & { existed: boolean }>(`${base}/items`, { label })
      .then((res) => ({ option: res.data, existed: res.existed })),

  /** Rename, hide or restore: the owner tier only. */
  updateItem: (optionId: string, patch: { label?: string; isActive?: boolean }) =>
    apiClient.patch<ApiResponse<ChecklistItemOption>>(`${base}/items/${encodeURIComponent(optionId)}`, patch).then((res) => res.data),

  /** Every item, hidden ones included, exactly once: the owner tier only. */
  reorderItems: (orderedIds: string[]) =>
    apiClient.put<ApiResponse<ChecklistItemOption[]>>(`${base}/items/order`, { orderedIds }).then((res) => res.data),
};
